import { useMemo, useState } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import Dialog from '../../components/Dialog.jsx';
import PageState from '../../components/PageState.jsx';
import { api } from '../../lib/api.js';
import { ACCESS_REVIEWER_ROLE, ADMIN_ROLE, AUDITOR_ROLE, TENANT_ROLES, VIEWER_ROLE } from '../../../../shared/solution.mjs';
import { auditorSelectableIn, viewerSelectableIn } from './people-format.jsx';
import { GUARDIAN_ROLE, ROLE_DESCRIPTIONS, ROLE_IDS, ROLE_LABELS, buildPeopleRows, isStaffEmail, manageable, membersOfRole, peopleSearchText } from '../../lib/people-rows.js';
import { AssignAllDialog, AssignAllResult } from './AssignAll.jsx';
import { AddMemberDialog, DialogError } from './PeopleDialogs.jsx';

const TONE = { green: 'badge-green', amber: 'badge-amber', red: 'badge-red', slate: 'badge-slate' };
const LAST_ADMIN = 'This customer needs at least one admin. Assign the admin role to someone else first.';

function RemoveDialog({ row, role, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const only = role !== GUARDIAN_ROLE && row.tenantRoles.length === 1;
  async function go() {
    setBusy(true);
    setError('');
    try {
      const done = await onConfirm();
      if (done) onClose();
    } catch (err) {
      // The dialog stays open with the reason (cap, invalid address, 403 ...).
      setError(typeof err?.message === 'string' && err.message.trim() !== '' ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      title={`Remove ${ROLE_LABELS[role]}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-danger" disabled={busy} onClick={go}>{busy ? 'Removing...' : `Remove ${ROLE_LABELS[role]} from ${row.email}`}</button>
        </>
      )}
    >
      {only ? <p>This is their only role, so their access is revoked.</p> : <p>{row.email} will no longer hold {ROLE_LABELS[role]}.</p>}
      <DialogError message={error} />
    </Dialog>
  );
}

function Members({ role, rows, admins, canRemove, onRemove }) {
  const columns = [
    { id: 'person', header: 'Person', sortValue: (r) => r.email.toLowerCase(), cell: (r) => <span className="break-all">{r.email}</span> },
    { id: 'origin', header: 'Origin', hideBelow: 'md', cell: (r) => r.origin },
    { id: 'status', header: 'Status', cell: (r) => <span className={TONE[r.status.tone]}>{r.status.label}</span> },
  ];
  const lastAdmin = (r) => role === ADMIN_ROLE && admins === 1 && r.status.key === 'active';
  return (
    <ConsoleTable
      label={`Members of ${ROLE_LABELS[role]}`}
      columns={columns}
      rows={rows}
      getRowId={(r) => r.id}
      rowName={(r) => r.email}
      searchText={peopleSearchText}
      searchPlaceholder={`Search members of ${ROLE_LABELS[role]}`}
      emptyState={{ title: 'No members', body: 'Nobody holds this role yet.' }}
      rowActions={(r) => [{
        id: 'remove',
        label: 'Remove from role',
        danger: true,
        hidden: !canRemove(r),
        disabled: lastAdmin(r),
        disabledReason: lastAdmin(r) ? LAST_ADMIN : undefined,
        onSelect: () => onRemove(r),
      }]}
    />
  );
}

export default function RolesTab({ customerId, data, guardians, canGuardian, onUpdate, onRevoke, onAssignAll, onChanged }) {
  const [expanded, setExpanded] = useState([]);
  const [removing, setRemoving] = useState(null); // { row, role }
  const [adding, setAdding] = useState(null); // role id
  const [assigning, setAssigning] = useState(null); // role id
  const auditorSelectable = auditorSelectableIn(data);
  const viewerSelectable = viewerSelectableIn(data);
  // A role the central IdP has not registered yet is never offered as a new grant (it can still be removed), and the
  // Access Reviews reviewer role never is (it follows each access review).
  const notOffered = (id) => (id === AUDITOR_ROLE && !auditorSelectable) || (id === VIEWER_ROLE && !viewerSelectable) || id === ACCESS_REVIEWER_ROLE;
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const people = useMemo(
    () => buildPeopleRows({ members: data.members ?? [], guardians: canGuardian ? guardians ?? [] : [] }),
    [data.members, guardians, canGuardian],
  );

  if (data.membersError) return <PageState state="error" title="The member list is unavailable right now" message="Try again in a moment." onRetry={onChanged} />;
  if (!data.members) return <PageState state="empty" title="No sign-in yet" message="No login has been created for this customer yet." />;

  const everyone = data.members.filter(manageable);
  const roleRows = ROLE_IDS.filter((id) => canGuardian || id !== GUARDIAN_ROLE)
    .map((id) => ({ id, label: ROLE_LABELS[id], description: ROLE_DESCRIPTIONS[id], members: membersOfRole(people, id) }));
  const adminCount = data.members.filter((m) => m.status === 'active' && m.roles.includes(ADMIN_ROLE)).length;
  const canRemove = (role) => (r) => (role === GUARDIAN_ROLE ? Boolean(r.guardian) : r.kind === 'member' && manageable(r.member));

  // A failed Guardian call refreshes the list, then rethrows so the open dialog shows the API's own message.
  async function guardianCall(call) {
    try {
      await call();
    } catch (err) {
      try { await onChanged(); } catch { /* the original error is the one to show */ }
      throw err;
    }
    await onChanged();
    return true;
  }

  async function remove({ row, role }) {
    if (role === GUARDIAN_ROLE) {
      return guardianCall(() => api.removeWatchGuardian(customerId, row.guardian.guardianId));
    }
    const m = row.member;
    // Removing a role keeps the member's others; removing the last one revokes the access.
    const rest = TENANT_ROLES.filter((r) => r !== role && m.roles.includes(r));
    if (rest.length === 0) return onRevoke(m.email);
    return onUpdate(m.email, rest, isStaffEmail(m.email) ? m.roles : undefined);
  }

  const addTenant = (member, role) => onUpdate(member.email, TENANT_ROLES.filter((r) => r === role || member.roles.includes(r)), isStaffEmail(member.email) ? member.roles : undefined);

  async function addGuardian(address) {
    return guardianCall(() => api.addWatchGuardian(customerId, address));
  }

  async function assignAll() {
    setBusy(true);
    try {
      const out = await onAssignAll(assigning);
      if (out) setResult(out);
    } finally {
      setBusy(false);
      setAssigning(null);
    }
  }

  const columns = [
    { id: 'role', header: 'Role', sortValue: (r) => r.label, cell: (r) => (<div><div className="font-medium text-ipc-ink">{r.label}</div><div className="text-xs text-ipc-muted">{r.description}</div></div>) },
    { id: 'members', header: 'Members', sortValue: (r) => r.members.length, align: 'right', cell: (r) => String(r.members.length) },
  ];

  const rowActions = (r) => {
    const tenant = r.id !== GUARDIAN_ROLE;
    const already = everyone.filter((m) => m.roles.includes(r.id)).length;
    const nothingToDo = everyone.length === 0 || already === everyone.length;
    return [
      {
        id: 'assign',
        label: 'Assign to everyone',
        hidden: !tenant || notOffered(r.id),
        disabled: nothingToDo,
        disabledReason: nothingToDo ? (everyone.length === 0 ? 'There is nobody to assign it to.' : 'Everyone already has this role.') : undefined,
        onSelect: () => { setResult(null); setAssigning(r.id); },
      },
      { id: 'add', label: 'Add member', hidden: notOffered(r.id), onSelect: () => setAdding(r.id) },
    ];
  };

  return (
    <div className="space-y-3" data-testid="roles-view">
      <ConsoleTable
        label="Roles"
        columns={columns}
        rows={roleRows}
        getRowId={(r) => r.id}
        rowName={(r) => r.label}
        expandedIds={expanded}
        onExpandedChange={setExpanded}
        renderExpanded={(r) => (
          <Members role={r.id} rows={r.members} admins={adminCount} canRemove={canRemove(r.id)} onRemove={(row) => setRemoving({ row, role: r.id })} />
        )}
        rowActions={rowActions}
      />
      {result ? <AssignAllResult result={result} /> : null}
      {removing ? <RemoveDialog row={removing.row} role={removing.role} onClose={() => setRemoving(null)} onConfirm={() => remove(removing)} /> : null}
      {adding ? (
        <AddMemberDialog
          role={adding}
          candidates={data.members.filter((m) => manageable(m) && !m.roles.includes(adding))}
          onClose={() => setAdding(null)}
          onAddTenant={addTenant}
          onAddGuardian={addGuardian}
        />
      ) : null}
      {assigning ? (
        <AssignAllDialog
          role={assigning}
          total={everyone.length}
          already={everyone.filter((m) => m.roles.includes(assigning)).length}
          busy={busy}
          onConfirm={assignAll}
          onCancel={() => setAssigning(null)}
        />
      ) : null}
    </div>
  );
}
